import {
  AuthError,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  type UserCredential,
} from "firebase/auth";
import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  deleteDoc,
  updateDoc,
  where,
  writeBatch,
  type DocumentReference,
  type DocumentSnapshot,
  type Firestore,
  type Timestamp,
} from "firebase/firestore";
import { tryInitFirebase, type FirebaseHandles } from "./firebase";
import { saveProfile as saveProfileLocal } from "./storage";

const LOCAL_UID = "local";

type FirebaseExports = {
  readonly app?: FirebaseHandles["app"];
  readonly auth?: FirebaseHandles["auth"];
  readonly db?: FirebaseHandles["db"];
  readonly storage?: FirebaseHandles["storage"];
};

let handlesCache: FirebaseHandles | null = null;

const resolveHandles = (): FirebaseHandles | null => {
  if (!handlesCache) {
    handlesCache = tryInitFirebase();
  }
  return handlesCache;
};

export const fb = {} as FirebaseExports;
Object.defineProperties(fb, {
  app: { enumerable: true, get: () => resolveHandles()?.app },
  auth: { enumerable: true, get: () => resolveHandles()?.auth },
  db: { enumerable: true, get: () => resolveHandles()?.db },
  storage: { enumerable: true, get: () => resolveHandles()?.storage },
});

export const hasFirebase = (): boolean => !!resolveHandles();

// ---- Super Admin ----
const SUPER_ADMIN_UIDS = [
  "ShEMxXLKysgaaeLx0odLbhJ68oQ2",
  // Add more super admin UIDs as needed
];

export function isSuperAdmin(uid?: string | null): boolean {
  if (!uid) return false;
  return SUPER_ADMIN_UIDS.includes(uid);
}

// ---- Profile model ----
export type Unit = "lb" | "kg";
export type Sport = "football" | "basketball";
export type Program = "boys" | "girls" | "coed";
export type Level = "varsity" | "juniorHigh";

export type Team =
  | "football-varsity"
  | "football-junior-high"
  | "girls-basketball-varsity"
  | "girls-basketball-junior-high"
  | "boys-basketball-varsity"
  | "boys-basketball-junior-high";

export type TeamDefinition = {
  id: Team;
  label: string;
  shortLabel: string;
  sport: Sport;
  program: Program;
  level: Level;
  legacy?: string[];
};

export const TEAM_DEFINITIONS: TeamDefinition[] = [
  {
    id: "football-varsity",
    label: "Football - Varsity",
    shortLabel: "Football Varsity",
    sport: "football",
    program: "coed",
    level: "varsity",
    legacy: ["varsity", "football varsity", "fb varsity"],
  },
  {
    id: "football-junior-high",
    label: "Football - Junior High",
    shortLabel: "Football JH",
    sport: "football",
    program: "coed",
    level: "juniorHigh",
    legacy: ["jh", "junior high", "football jh"],
  },
  {
    id: "girls-basketball-varsity",
    label: "Girls Basketball - Varsity",
    shortLabel: "Girls BB Varsity",
    sport: "basketball",
    program: "girls",
    level: "varsity",
    legacy: ["girls basketball varsity", "girls bball varsity"],
  },
  {
    id: "girls-basketball-junior-high",
    label: "Girls Basketball - Junior High",
    shortLabel: "Girls BB JH",
    sport: "basketball",
    program: "girls",
    level: "juniorHigh",
    legacy: ["girls basketball junior high", "girls bball jh"],
  },
  {
    id: "boys-basketball-varsity",
    label: "Boys Basketball - Varsity",
    shortLabel: "Boys BB Varsity",
    sport: "basketball",
    program: "boys",
    level: "varsity",
    legacy: ["boys basketball varsity", "boys bball varsity"],
  },
  {
    id: "boys-basketball-junior-high",
    label: "Boys Basketball - Junior High",
    shortLabel: "Boys BB JH",
    sport: "basketball",
    program: "boys",
    level: "juniorHigh",
    legacy: ["boys basketball junior high", "boys bball jh"],
  },
];

const TEAM_LOOKUP: Record<string, Team> = TEAM_DEFINITIONS.reduce(
  (acc, definition) => {
    acc[definition.id.toLowerCase()] = definition.id;
    definition.legacy?.forEach((alias) => {
      acc[alias.toLowerCase()] = definition.id;
    });
    return acc;
  },
  {} as Record<string, Team>
);

export function normalizeTeam(value: unknown): Team | undefined {
  if (typeof value !== "string") return undefined;
  const key = value.trim().toLowerCase();
  if (!key) return undefined;
  return TEAM_LOOKUP[key];
}

export function getTeamDefinition(team?: Team | string | null): TeamDefinition | undefined {
  if (!team) return undefined;
  const key = typeof team === "string" ? normalizeTeam(team) : team;
  if (!key) return undefined;
  return TEAM_DEFINITIONS.find((definition) => definition.id === key);
}

export function formatTeamLabel(team?: Team | string | null, fallback = "Not set"): string {
  if (!team) return fallback;
  const definition = getTeamDefinition(team);
  if (definition) {
    return definition.label;
  }
  if (typeof team === "string" && team.trim()) {
    return team;
  }
  return fallback;
}

export function getTeamGroup(team?: Team | string | null): TeamDefinition[] {
  const definition = getTeamDefinition(team);
  if (!definition) {
    return TEAM_DEFINITIONS.filter(
      (candidate) => candidate.sport === "football" && candidate.program === "coed"
    );
  }
  return TEAM_DEFINITIONS.filter(
    (candidate) =>
      candidate.sport === definition.sport && candidate.program === definition.program
  );
}

export function getTeamGroupIds(team?: Team | string | null): Team[] {
  const group = getTeamGroup(team);
  return (group.length ? group : TEAM_DEFINITIONS).map((definition) => definition.id);
}

const DEFAULT_TEAM_SCOPE: Team[] = getTeamGroupIds("football-varsity");

export function getStoredTeamSelection(): Team | "" {
  if (typeof window === "undefined") return "";
  try {
    const stored = window.localStorage.getItem("pl-strength-team");
    const normalized = normalizeTeam(stored ?? undefined);
    return normalized ?? "";
  } catch {
    return "";
  }
}

export function setStoredTeamSelection(team: Team | ""): void {
  if (typeof window === "undefined") return;
  if (team) {
    window.localStorage.setItem("pl-strength-team", team);
  } else {
    window.localStorage.removeItem("pl-strength-team");
  }
  window.dispatchEvent(
    new CustomEvent<Team | null>("pl-team-change", { detail: team || null })
  );
}

export function resolveTeamScopes(team?: Team | string | null): Team[] {
  const scopes = getTeamGroupIds(team);
  return scopes.length ? scopes : DEFAULT_TEAM_SCOPE;
}

const TEAM_SCOPES_STORAGE_KEY = "pl-strength-team-scopes";

export function getStoredTeamScopes(): Team[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(TEAM_SCOPES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((value) => normalizeTeam(value) ?? null)
      .filter((value): value is Team => Boolean(value));
  } catch {
    return [];
  }
}

export function setStoredTeamScopes(scopes: Team[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(TEAM_SCOPES_STORAGE_KEY, JSON.stringify(scopes));
  } catch {
    // ignore storage errors
  }
  window.dispatchEvent(
    new CustomEvent<Team[]>("pl-team-scopes-change", { detail: scopes })
  );
}

export async function fetchCoachTeamScopes(uid?: string | null): Promise<Team[]> {
  const handles = resolveHandles();
  const auth = handles?.auth;
  const database = handles?.db;
  const resolvedUid = uid ?? auth?.currentUser?.uid ?? null;
  if (!database || !resolvedUid) return [];
  try {
    const snap = await getDoc(roleRef(database, resolvedUid));
    if (!snap.exists()) return [];
    const data = snap.data() as any;
    if (!Array.isArray(data?.teamScopes)) return [];
    return data.teamScopes
      .map((value: unknown) => normalizeTeam(value) ?? null)
      .filter((value: Team | null): value is Team => Boolean(value));
  } catch {
    return [];
  }
}

type AccessCodeHistory = {
  roles: string[];
  teamScopes: Team[];
  teamAnchor: string | null;
  lastUsed: Timestamp;
};

export type RolesDocument = {
  roles?: string[];
  teamScopes?: Team[];
  teamAnchor?: string | null;
  accessHistory?: Record<string, AccessCodeHistory>;
};

export async function updateCoachTeamScope(team: Team | "", accessCode?: string): Promise<void> {
  const handles = resolveHandles();
  const auth = handles?.auth;
  const database = handles?.db;
  const uid = auth?.currentUser?.uid;
  if (!database || !uid) return;
  
  const normalized = team ? normalizeTeam(team) : null;
  const ref = roleRef(database, uid);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? (snap.data() as RolesDocument) : null;
  const currentScopes = sanitizeTeamScopeArray(existing?.teamScopes);

  let nextScopes = currentScopes;
  if (normalized && !nextScopes.includes(normalized)) {
    nextScopes = [...nextScopes, normalized];
  }
  if (!nextScopes.length && normalized) {
    nextScopes = [normalized];
  }
  // If still no scopes, set defaults
  if (!nextScopes.length) {
    nextScopes = ["football-varsity", "football-junior-high"];
  }

  const teamAnchor = normalized ?? existing?.teamAnchor ?? null;
  const payload: Record<string, unknown> = {
    teamAnchor,
    updatedAt: serverTimestamp(),
  };

  // Always include teamScopes since we now have defaults
  payload.teamScopes = nextScopes;

  if (accessCode) {
    const currentRoles = await fetchRoles();
    const accessHistory = existing?.accessHistory || {};
    payload.accessHistory = {
      ...accessHistory,
      [accessCode]: {
        roles: currentRoles,
        teamScopes: normalized ? [normalized] : nextScopes,
        teamAnchor: normalized,
        lastUsed: serverTimestamp()
      }
    };
  }

  await setDoc(ref, payload, { merge: true });
}
export type BarOption = {
  id: string;
  label: string;
  weight: number;
};

export type EquipmentSettings = {
  plates: Record<Unit, number[]>;
  bars: Record<Unit, BarOption[]>;
  activeBarId: Record<Unit, string | null>;
};

export type Profile = {
  uid: string;
  firstName: string;
  lastName: string;
  unit: Unit;
  team?: Team;
  tm?: { bench?: number; squat?: number; deadlift?: number; press?: number };
  oneRm?: { bench?: number; squat?: number; deadlift?: number; press?: number };
  accessCode?: string | null;
  equipment?: EquipmentSettings;
  currentWeek?: 1 | 2 | 3 | 4;
  orgId?: string;
  orgCode?: string;
};

const DEFAULT_PLATES: Record<Unit, number[]> = {
  lb: [45, 35, 25, 10, 5, 2.5, 1.25],
  kg: [25, 20, 15, 10, 5, 2.5, 1.25, 0.5],
};

const DEFAULT_BAR_OPTIONS: Record<Unit, BarOption[]> = {
  lb: [
    { id: "bar-lb-standard-45", label: "Standard (45 lb)", weight: 45 },
    { id: "bar-lb-short-35", label: "Short (35 lb)", weight: 35 },
    { id: "bar-lb-ez-20", label: "EZ Bar (20 lb)", weight: 20 },
  ],
  kg: [
    { id: "bar-kg-standard-20", label: "Standard (20 kg)", weight: 20 },
    { id: "bar-kg-trainer-15", label: "Trainer (15 kg)", weight: 15 },
    { id: "bar-kg-technique-10", label: "Technique (10 kg)", weight: 10 },
  ],
};

const DEFAULT_ACTIVE_BAR: Record<Unit, string | null> = {
  lb: DEFAULT_BAR_OPTIONS.lb[0]?.id ?? null,
  kg: DEFAULT_BAR_OPTIONS.kg[0]?.id ?? null,
};

const cloneDefaultEquipment = (): EquipmentSettings => ({
  plates: {
    lb: [...DEFAULT_PLATES.lb],
    kg: [...DEFAULT_PLATES.kg],
  },
  bars: {
    lb: DEFAULT_BAR_OPTIONS.lb.map((bar) => ({ ...bar })),
    kg: DEFAULT_BAR_OPTIONS.kg.map((bar) => ({ ...bar })),
  },
  activeBarId: { ...DEFAULT_ACTIVE_BAR },
});

const cleanLabel = (value: string): string => {
  const trimmed = (value ?? "").trim();
  return trimmed.length ? trimmed.slice(0, 80) : "";
};

const makeBarId = (unit: Unit, weight: number, label: string): string => {
  const base = cleanLabel(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const slug = base || "bar";
  return `bar-${unit}-${String(weight).replace(/\D+/g, "")}-${slug}`;
};

const normalizePlateList = (list: number[] | undefined, unit: Unit): number[] => {
  if (!Array.isArray(list)) {
    return [...DEFAULT_PLATES[unit]];
  }
  const source = list;
  const unique = Array.from(
    new Set(
      source
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)
        .map((value) => Number(value.toFixed(3)))
    )
  );
  if (!unique.length) return [];
  unique.sort((a, b) => b - a);
  return unique;
};

const normalizeBarOptions = (bars: BarOption[] | undefined, unit: Unit): BarOption[] => {
  const source = Array.isArray(bars) ? bars : [];
  const acc = new Map<string, BarOption>();
  source.forEach((item) => {
    const weight = Number(item?.weight);
    if (!Number.isFinite(weight) || weight <= 0) return;
    const label = cleanLabel(item?.label ?? "") || `${weight} ${unit} bar`;
    const id =
      typeof item?.id === "string" && item.id.trim()
        ? item.id
        : makeBarId(unit, weight, label);
    acc.set(id, {
      id,
      label,
      weight: Number(weight.toFixed(2)),
    });
  });
  if (!acc.size) {
    if (Array.isArray(bars)) return [];
    return DEFAULT_BAR_OPTIONS[unit].map((bar) => ({ ...bar }));
  }
  return Array.from(acc.values()).sort((a, b) => b.weight - a.weight);
};

export const defaultEquipment = (): EquipmentSettings => cloneDefaultEquipment();

export const normalizeEquipment = (
  input?: EquipmentSettings | null
): EquipmentSettings => {
  const base = cloneDefaultEquipment();
  if (!input) return base;

  const result: EquipmentSettings = {
    plates: {
      lb: normalizePlateList(input.plates?.lb, "lb"),
      kg: normalizePlateList(input.plates?.kg, "kg"),
    },
    bars: {
      lb: normalizeBarOptions(input.bars?.lb, "lb"),
      kg: normalizeBarOptions(input.bars?.kg, "kg"),
    },
    activeBarId: { ...base.activeBarId },
  };

  (["lb", "kg"] as Unit[]).forEach((unit) => {
    const preferred = input.activeBarId?.[unit];
    const hasPreferred = preferred
      ? result.bars[unit].some((bar) => bar.id === preferred)
      : false;
    result.activeBarId[unit] = hasPreferred
      ? preferred!
      : result.bars[unit][0]?.id ?? null;
  });

  return result;
};

// Profiles are stored at root level: profiles/{uid}
const profRef = (database: Firestore, uid: string) =>
  doc(database, "profiles", uid);

let ensurePromise: Promise<string> | null = null;
let roleCache: string[] | null = null;
let rolePromise: Promise<string[]> | null = null;

type RoleListener = (roles: string[]) => void;

const roleListeners = new Set<RoleListener>();

const canonicalizeRoles = (roles: string[]): string[] =>
  Array.from(
    new Set(
      roles
        .map((role) => (typeof role === "string" ? role.trim().toLowerCase() : ""))
        .filter(Boolean)
    )
  ).sort();

const rolesMatch = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

const emitRoleChange = (roles: string[]) => {
  roleListeners.forEach((listener) => {
    try {
      listener([...roles]);
    } catch (err) {
      console.warn("Role listener failed", err);
    }
  });
};

const applyRoleCache = (roles: string[], forceEmit = false) => {
  const canonical = canonicalizeRoles(roles);
  const changed = !roleCache || !rolesMatch(roleCache, canonical);
  roleCache = canonical;
  if (changed || forceEmit) {
    emitRoleChange(canonical);
  }
};

const clearRoleCache = () => {
  roleCache = null;
  emitRoleChange([]);
};

export function subscribeToRoleChanges(listener: RoleListener): () => void {
  roleListeners.add(listener);
  if (roleCache) {
    try {
      listener([...roleCache]);
    } catch (err) {
      console.warn("Role listener failed", err);
    }
  }
  return () => {
    roleListeners.delete(listener);
  };
}

export async function ensureAnon(): Promise<string> {
  const handles = resolveHandles();
  const auth = handles?.auth;
  if (!auth) return LOCAL_UID;
  if (auth.currentUser?.uid) return auth.currentUser.uid;
  if (ensurePromise) return ensurePromise;

  ensurePromise = new Promise<string>((resolve) => {
    const finish = (value: string) => {
      ensurePromise = null;
      resolve(value);
    };
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user?.uid) {
        unsub();
        finish(user.uid);
      }
    });
  });

  return ensurePromise;
}

// Roles are stored at root level: roles/{uid}
const roleRef = (database: Firestore, uid: string) =>
  doc(database, "roles", uid);

const sanitizeRoleArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return canonicalizeRoles(value);
};

const sanitizeTeamScopeArray = (value: unknown): Team[] => {
  if (!Array.isArray(value)) return [];
  const scoped = value
    .map((entry) => normalizeTeam(entry) ?? null)
    .filter((entry): entry is Team => Boolean(entry));
  return Array.from(new Set(scoped));
};

const teamScopesEqual = (a: Team[], b: Team[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
};

const deriveFallbackTeamScopes = (data: any): Team[] => {
  const anchor = normalizeTeam(
    data?.teamAnchor ?? data?.team ?? data?.lastTeam ?? null
  );
  if (anchor) {
    return resolveTeamScopes(anchor);
  }
  return DEFAULT_TEAM_SCOPE;
};

const maybeRepairLegacyRoleDoc = async (
  database: Firestore,
  uid: string,
  normalizedRoles: string[],
  data: any
) => {
  if (!database) return;
  const storedRoles = sanitizeRoleArray(data?.roles);
  const needsRoleRepair =
    normalizedRoles.length > 0 && !rolesMatch(storedRoles, normalizedRoles);

  const storedScopes = sanitizeTeamScopeArray(data?.teamScopes);
  const needsTeamScopeRepair =
    storedScopes.length === 0 &&
    (normalizedRoles.includes("coach") || normalizedRoles.includes("admin"));

  if (!needsRoleRepair && !needsTeamScopeRepair) return;

  const payload: Record<string, unknown> = {
    updatedAt: serverTimestamp(),
  };

  if (needsRoleRepair) {
    payload.roles = normalizedRoles;
  }
  if (needsTeamScopeRepair) {
    payload.teamScopes = deriveFallbackTeamScopes(data);
  }

  try {
    await setDoc(roleRef(database, uid), payload, { merge: true });
  } catch (err) {
    console.warn("Failed to repair legacy role doc", err);
  }
};

const normalizeRoles = (raw: any): string[] => {
  if (!raw) return [];
  const baseList: string[] = Array.isArray(raw.roles)
    ? raw.roles
    : raw.role
    ? [raw.role]
    : [];
  const truthyKeys =
    typeof raw === "object" && raw
      ? Object.entries(raw)
          .filter(([key, value]) => {
            if (key === "roles" || key === "role") return false;
            if (typeof value === "boolean") return value;
            if (typeof value === "string") {
              const normalized = value.trim().toLowerCase();
              return normalized === "true" || normalized === "yes" || normalized === "1";
            }
            return false;
          })
          .map(([key]) => key)
      : [];
  const roles = [...baseList, ...truthyKeys];
  return canonicalizeRoles(roles);
};

async function fetchRoles(): Promise<string[]> {
  if (roleCache) return roleCache;
  if (rolePromise) return rolePromise;

  const handles = resolveHandles();
  const auth = handles?.auth;
  const database = handles?.db;
  const uid = auth?.currentUser?.uid;
  if (!database || !uid) {
    applyRoleCache([]);
    return [];
  }

  rolePromise = (async () => {
    try {
      const snap = await getDoc(roleRef(database, uid));
      const data = snap.data();
      const roles = snap.exists() ? normalizeRoles(data) : [];
      applyRoleCache(roles);
      if (snap.exists()) {
        await maybeRepairLegacyRoleDoc(database, uid, roles, data);
      }
      return roles;
    } finally {
      rolePromise = null;
    }
  })();

  return rolePromise;
}

export async function refreshRoles(targetUid?: string): Promise<string[]> {
  const handles = resolveHandles();
  const auth = handles?.auth;
  const database = handles?.db;
  const uid = targetUid ?? auth?.currentUser?.uid;
  if (!database || !uid) {
    applyRoleCache([], true);
    return [];
  }
  const snap = await getDoc(roleRef(database, uid));
  const data = snap.data();
  const roles = snap.exists() ? normalizeRoles(data) : [];
  applyRoleCache(roles, true);  // Force emit to notify all subscribers
  if (snap.exists()) {
    await maybeRepairLegacyRoleDoc(database, uid, roles, data);
  }
  return roles;
}

async function setCurrentUserRoles(nextRoles: string[]): Promise<string[]> {
  const handles = resolveHandles();
  const auth = handles?.auth;
  const database = handles?.db;
  const uid = auth?.currentUser?.uid;
  if (!database || !uid) return [];

  const roles = canonicalizeRoles(nextRoles);

  const payload: Record<string, any> = {
    roles,
    updatedAt: serverTimestamp(),
  };

  await setDoc(roleRef(database, uid), payload, { merge: true });
  applyRoleCache(roles);
  return roles;
}

export async function getUid(): Promise<string | null> {
  const uid = await ensureAnon();
  if (!uid || uid === LOCAL_UID) return null;
  return uid;
}

export async function loadProfileRemote(uid?: string): Promise<Profile | null> {
  const targetUid = uid ?? await getUid();
  if (!targetUid) return null;
  const handles = resolveHandles();
  const database = handles?.db;
  if (!database) return null;
  const snap = await getDoc(profRef(database, targetUid));
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  return {
    uid: targetUid,
    firstName: data.firstName || "",
    lastName: data.lastName || "",
    unit: (data.unit || "lb") as Unit,
    team: normalizeTeam(data.team),
    tm: data.tm || {},
    oneRm: data.oneRm || {},
    accessCode: data.accessCode ?? null,
    equipment: normalizeEquipment(data.equipment as EquipmentSettings | undefined),
  };
}

export async function saveProfile(p: Profile, options?: { skipLocal?: boolean }) {
  const handles = resolveHandles();
  const database = handles?.db;
  const normalizedEquipment = normalizeEquipment(p.equipment);
  const normalizedProfile: Profile = {
    ...p,
    oneRm: p.oneRm || {},
    equipment: normalizedEquipment,
  };
  if (!database) {
    if (options?.skipLocal) {
      throw new Error("Firebase is not available to sync this profile right now.");
    }
    saveProfileLocal(normalizedProfile);
    return;
  }
  const ref = profRef(database, p.uid);
  const payload: Record<string, any> = {
    firstName: normalizedProfile.firstName || "",
    lastName: normalizedProfile.lastName || "",
    unit: normalizedProfile.unit || "lb",
    team: normalizedProfile.team || null,
    tm: normalizedProfile.tm || {},
    oneRm: normalizedProfile.oneRm || {},
    accessCode: normalizedProfile.accessCode ?? null,
    equipment: normalizedEquipment,
  };
  // Include orgId and orgCode if provided - required for roster filtering
  if (normalizedProfile.orgId) {
    payload.orgId = normalizedProfile.orgId;
  }
  if (normalizedProfile.orgCode) {
    payload.orgCode = normalizedProfile.orgCode;
  }
  if (normalizedProfile.currentWeek) {
    payload.currentWeek = normalizedProfile.currentWeek;
  }
  const snap = await getDoc(ref);
  if (snap.exists()) await updateDoc(ref, payload);
  else await setDoc(ref, payload);
  if (!options?.skipLocal) {
    saveProfileLocal(normalizedProfile);
  }
}

// Update athlete's current week
export async function updateAthleteWeek(uid: string, week: 1 | 2 | 3 | 4): Promise<void> {
  const handles = resolveHandles();
  const database = handles?.db;
  if (!database) throw new Error("Firebase not available");
  
  const ref = profRef(database, uid);
  await updateDoc(ref, { currentWeek: week });
}

// Calculate TM increase suggestions based on Week 4 AMRAP performance
export async function calculateTMSuggestions(uid: string): Promise<{
  bench?: number;
  squat?: number;
  deadlift?: number;
  press?: number;
}> {
  const profile = await loadProfileRemote(uid);
  if (!profile || !profile.tm) return {};
  
  const sessions = await fetchAthleteSessions(uid);
  const week4Sessions = sessions.filter(s => s.week === 4);
  
  const suggestions: Record<string, number> = {};
  
  (['bench', 'squat', 'deadlift', 'press'] as const).forEach(lift => {
    const currentTM = profile.tm?.[lift];
    if (!currentTM) return;
    
    // Find most recent Week 4 session for this lift
    const recentWeek4 = week4Sessions
      .filter(s => s.lift === lift)
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0];
    
    if (!recentWeek4) return;
    
    // Week 4 is deload - check if they're ready to progress
    // Standard progression: +5 lb or +2.5 kg for upper, +10 lb or +5 kg for lower
    const unit = profile.unit || 'lb';
    const isLower = lift === 'squat' || lift === 'deadlift';
    
    if (unit === 'lb') {
      suggestions[lift] = currentTM + (isLower ? 10 : 5);
    } else {
      suggestions[lift] = currentTM + (isLower ? 5 : 2.5);
    }
  });
  
  return suggestions;
}

// Advance entire cycle - move to Week 1 and optionally increase TMs
export async function advanceCycle(uid: string, tmIncreases?: {
  bench?: number;
  squat?: number;
  deadlift?: number;
  press?: number;
}): Promise<void> {
  const profile = await loadProfileRemote(uid);
  if (!profile) throw new Error("Profile not found");
  
  const updatedProfile: Profile = {
    ...profile,
    currentWeek: 1,
  };
  
  if (tmIncreases) {
    updatedProfile.tm = {
      ...profile.tm,
      ...tmIncreases,
    };
  }
  
  await saveProfile(updatedProfile, { skipLocal: true });
}

export const normalizePasscodeDigits = (code: string): string =>
  code.replace(/\D+/g, "").slice(0, 4);

export const buildAthleteEmail = (firstName: string, lastName: string, orgId: string): string => {
  const canonical = `${firstName}${lastName}`.toLowerCase().replace(/[^a-z]/g, "");
  const safeOrgId = (orgId || "pl").toLowerCase().replace(/[^a-z0-9]/g, "");
  // Use a proper email format that Firebase accepts
  return `${canonical}-${safeOrgId}@anchorone.app`;
};

const passcodeToPassword = (code: string, orgCode: string) => {
  // Firebase requires passwords to be at least 6 characters
  const base = `${orgCode}-${code}`;
  return base.length >= 6 ? base : base + "!athlete";
};

export class AthleteAuthError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = "AthleteAuthError";
  }
}

export type AthleteSignInOptions = {
  firstName: string;
  lastName: string;
  passcodeDigits: string;
  team?: Team | "";
  orgId: string;
  orgCode: string;
  isFirstTime?: boolean; // Must be true to create new account
};

export type AthleteSignInResult = {
  profile: Profile;
  createdAccount: boolean;
  credential: UserCredential | null;
};

export async function signInOrCreateAthleteAccount(
  options: AthleteSignInOptions
): Promise<AthleteSignInResult> {
  const auth = fb.auth;
  if (!auth) {
    throw new AthleteAuthError("auth/unavailable", "Firebase auth is unavailable.");
  }

  const first = options.firstName.trim();
  const last = options.lastName.trim();
  const code = options.passcodeDigits.trim();
  const orgId = options.orgId.trim();
  const orgCode = options.orgCode.trim();
  const allowCreate = options.isFirstTime === true;

  const email = buildAthleteEmail(first, last, orgId);
  const password = passcodeToPassword(code, orgCode);

  let credential: UserCredential | null = null;
  let createdAccount = false;

  try {
    credential = await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    const error = err as AuthError;
    const userNotFound =
      error.code === "auth/user-not-found" || error.code === "auth/invalid-credential";
    
    if (userNotFound) {
      // Only create account if user explicitly marked "first time" and provided org code
      if (!allowCreate) {
        throw new AthleteAuthError(
          "auth/user-not-found",
          "No account found. If this is your first time, check the 'First time?' box and enter your team code."
        );
      }
      credential = await createUserWithEmailAndPassword(auth, email, password);
      createdAccount = true;
    } else if (error.code === "auth/wrong-password") {
      throw new AthleteAuthError("auth/wrong-password", "Incorrect passcode.");
    } else {
      throw error;
    }
  }

  const uid = credential?.user?.uid ?? auth.currentUser?.uid;
  if (!uid) {
    throw new AthleteAuthError("auth/internal-error", "We could not sign you in.");
  }

  let existingProfile: Profile | null = null;
  try {
    existingProfile = await loadProfileRemote(uid);
  } catch (err) {
    console.warn("Failed to load existing profile before athlete save", err);
  }

  const codeStatus = await ensureAthleteCode(
    uid,
    code,
    orgId,
    existingProfile?.accessCode ?? null
  );

  if (codeStatus === "taken") {
    if (createdAccount && auth.currentUser) {
      try {
        await auth.currentUser.delete();
      } catch (deleteErr) {
        console.warn("Failed to remove newly created user after code conflict", deleteErr);
      }
    }
    try {
      await auth.signOut();
    } catch (signOutErr) {
      console.warn("Failed to sign out after code conflict", signOutErr);
    }
    throw new AthleteAuthError(
      "athlete-code/taken",
      "That code is already being used by another athlete."
    );
  }

  if (codeStatus === "unavailable") {
    throw new AthleteAuthError(
      "athlete-code/unavailable",
      "We could not verify that code. Try again shortly."
    );
  }

  const resolvedTeam = options.team || existingProfile?.team;
  const profile: Profile = {
    uid,
    firstName: first,
    lastName: last,
    unit: existingProfile?.unit ?? "lb",
    team: resolvedTeam,
    tm: existingProfile?.tm ?? {},
    accessCode: code,
    equipment: normalizeEquipment(existingProfile?.equipment),
    orgId,
    orgCode,
  };

  await saveProfile(profile);

  return {
    profile,
    createdAccount,
    credential,
  };
}

// listRoster via profiles collection
export type RosterEntry = {
  uid: string;
  firstName?: string;
  lastName?: string;
  unit?: Unit;
  team?: Team;
  accessCode?: string | null;
  roles?: string[];
  orgId?: string;
};

export async function listRoster(orgId?: string): Promise<RosterEntry[]> {
  const handles = resolveHandles();
  const database = handles?.db;
  const auth = handles?.auth;
  if (!database) return [];
  try {
    await ensureAnon();
  } catch (err) {
    console.warn("ensureAnon failed before listRoster", err);
  }
  
  // Get current user's orgId if not provided
  let filterOrgId = orgId;
  if (!filterOrgId && auth?.currentUser?.uid) {
    try {
      const userProfileSnap = await getDoc(profRef(database, auth.currentUser.uid));
      if (userProfileSnap.exists()) {
        filterOrgId = userProfileSnap.data().orgId;
      }
    } catch (err) {
      console.warn("Failed to get user orgId for roster filter", err);
    }
  }
  
  // Query root profiles collection
  const profilesRef = collection(database, "profiles");
  let q;
  if (filterOrgId) {
    q = query(profilesRef, where("orgId", "==", filterOrgId));
  } else {
    q = profilesRef;
  }
  const snap = await getDocs(q);

  const rows = await Promise.all(
    snap.docs.map(async (docSnap) => {
      const data = docSnap.data();
      const uid = docSnap.id || data.uid;
      let roles: string[] = [];
      try {
        const roleSnap = await getDoc(roleRef(database, uid));
        roles = roleSnap.exists() ? normalizeRoles(roleSnap.data()) : [];
      } catch (err) {
        console.warn(`Failed to load roles for ${uid}`, err);
      }

      return {
        uid,
        firstName: data.firstName,
        lastName: data.lastName,
        unit: data.unit as Unit,
        team: normalizeTeam(data.team),
        accessCode: data.accessCode ?? null,
        roles,
        orgId: data.orgId,
      };
    })
  );

  return rows;
}

// ---- Attendance sheets ----

export type AttendanceLevel = Team;

export type AttendanceAthlete = {
  id: string;
  firstName: string;
  lastName: string;
  level: AttendanceLevel;
};

export type AttendanceSheet = {
  team: Team;
  dates: string[];
  athletes: AttendanceAthlete[];
  records: Record<string, Record<string, boolean>>;
  updatedAt?: number;
};

const ATTENDANCE_STORAGE_PREFIX = "pl.attendance.";

const attendanceStorageKey = (team: Team): string =>
  `${ATTENDANCE_STORAGE_PREFIX}${team}`;

const defaultAttendanceSheet = (team: Team): AttendanceSheet => ({
  team,
  dates: [],
  athletes: [],
  records: {},
  updatedAt: undefined,
});

const readAttendanceLocal = (team: Team): AttendanceSheet | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(attendanceStorageKey(team));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return normalizeAttendanceSheet(parsed, team);
  } catch (_) {
    return null;
  }
};

const writeAttendanceLocal = (sheet: AttendanceSheet) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      attendanceStorageKey(sheet.team),
      JSON.stringify(sheet)
    );
  } catch (_) {
    // ignore storage issues
  }
};

const sanitizeName = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim().slice(0, 60);
  return trimmed;
};

const normalizeAttendanceRecords = (
  athletes: AttendanceAthlete[],
  dates: string[],
  raw: unknown
): Record<string, Record<string, boolean>> => {
  const records: Record<string, Record<string, boolean>> = {};
  const source =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const dateSet = new Set(dates);
  athletes.forEach((athlete) => {
    const athleteSource = source[athlete.id];
    const row: Record<string, boolean> = {};
    if (
      athleteSource &&
      typeof athleteSource === "object" &&
      !Array.isArray(athleteSource)
    ) {
      Object.entries(athleteSource as Record<string, unknown>).forEach(
        ([date, value]) => {
          if (dateSet.has(date)) {
            row[date] = value === true;
          }
        }
      );
    }
    dates.forEach((date) => {
      if (!(date in row)) {
        row[date] = false;
      }
    });
    records[athlete.id] = row;
  });
  return records;
};

const normalizeAttendanceSheet = (
  input: any,
  team: Team
): AttendanceSheet => {
  const rawDates = Array.isArray(input?.dates) ? input.dates : [];
  const dates: string[] = Array.from(
    new Set(
      rawDates
        .map((value: unknown) =>
          typeof value === "string" ? value.trim().slice(0, 40) : ""
        )
        .filter((value: string) => value.length > 0)
    )
  );

  const rawAthletes = Array.isArray(input?.athletes) ? input.athletes : [];
  const athletes: AttendanceAthlete[] = rawAthletes
    .map((item: any) => {
      if (!item || typeof item !== "object") return null;
      const id =
        typeof item.id === "string" && item.id.trim()
          ? item.id.trim()
          : null;
      const firstName = sanitizeName((item as any).firstName);
      const lastName = sanitizeName((item as any).lastName);
      const level = normalizeTeam((item as any).level) ?? team;
      if (!id || (!firstName && !lastName)) return null;
      return { id, firstName, lastName, level };
    })
    .filter((item: any): item is AttendanceAthlete => item !== null);

  const updatedAtRaw = input?.updatedAt;
  let updatedAt: number | undefined;
  if (typeof updatedAtRaw === "number" && Number.isFinite(updatedAtRaw)) {
    updatedAt = updatedAtRaw;
  } else if (
    updatedAtRaw &&
    typeof (updatedAtRaw as any).toMillis === "function"
  ) {
    try {
      updatedAt = (updatedAtRaw as any).toMillis();
    } catch (_) {
      updatedAt = undefined;
    }
  }

  return {
    team,
    dates,
    athletes,
    records: normalizeAttendanceRecords(
      athletes,
      dates,
      input?.records ?? {}
    ),
    updatedAt,
  };
};

const attendanceDocRef = (database: Firestore, team: Team) =>
  doc(database, "attendance", team);

export async function loadAttendanceSheet(
  team: Team
): Promise<AttendanceSheet> {
  const handles = resolveHandles();
  const database = handles?.db;
  if (!database) {
    return readAttendanceLocal(team) ?? defaultAttendanceSheet(team);
  }

  try {
    const ref = attendanceDocRef(database, team);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      const fallback = readAttendanceLocal(team) ?? defaultAttendanceSheet(team);
      return fallback;
    }
    const normalized = normalizeAttendanceSheet(snap.data(), team);
    writeAttendanceLocal(normalized);
    return normalized;
  } catch (err) {
    console.warn(`loadAttendanceSheet failed for ${team}`, err);
    return readAttendanceLocal(team) ?? defaultAttendanceSheet(team);
  }
}

export async function saveAttendanceSheet(
  sheet: AttendanceSheet
): Promise<void> {
  const handles = resolveHandles();
  const database = handles?.db;

  const cleanDates = Array.from(
    new Set(sheet.dates.map((value) => value.trim()).filter(Boolean))
  );
  const cleanAthletes = sheet.athletes
    .map((athlete) => ({
      ...athlete,
      firstName: sanitizeName(athlete.firstName),
      lastName: sanitizeName(athlete.lastName),
      level: normalizeTeam(athlete.level) ?? sheet.team,
    }))
    .filter((athlete) => athlete.id && (athlete.firstName || athlete.lastName));

  const cleanRecords = normalizeAttendanceRecords(
    cleanAthletes,
    cleanDates,
    sheet.records
  );

  const payload = {
    dates: cleanDates,
    athletes: cleanAthletes,
    records: cleanRecords,
    updatedAt: serverTimestamp(),
  };

  if (database) {
    const ref = attendanceDocRef(database, sheet.team);
    await setDoc(ref, payload, { merge: true });
  }

  writeAttendanceLocal({
    team: sheet.team,
    dates: cleanDates,
    athletes: cleanAthletes,
    records: cleanRecords,
    updatedAt: Date.now(),
  });
}

export type AccessHistory = {
  code: string;
  roles: string[];
  teamScopes: string[];
  teamAnchor: string | null;
  lastUsed: Timestamp;
};

type AccessHistoryRecord = Record<string, {
  roles: string[];
  teamScopes: string[];
  teamAnchor: string | null;
  lastUsed: Timestamp;
}>;

export async function getAccessHistory(): Promise<AccessHistory[]> {
  const handles = resolveHandles();
  const auth = handles?.auth;
  const database = handles?.db;
  const uid = auth?.currentUser?.uid;
  if (!database || !uid) return [];

  const ref = roleRef(database, uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return [];

  const data = snap.data() as RolesDocument;
  if (!data.accessHistory) return [];

  const history = data.accessHistory as AccessHistoryRecord;
  return Object.entries(history).map(([code, record]) => ({
    code,
    roles: record.roles,
    teamScopes: record.teamScopes,
    teamAnchor: record.teamAnchor,
    lastUsed: record.lastUsed
  }));
}

export async function clearAccessHistory(): Promise<void> {
  const handles = resolveHandles();
  const auth = handles?.auth;
  const database = handles?.db;
  const uid = auth?.currentUser?.uid;
  if (!database || !uid) return;

  const ref = roleRef(database, uid);
  await updateDoc(ref, {
    accessHistory: {},
    updatedAt: serverTimestamp()
  });
}

export async function getCurrentRoles(): Promise<string[]> {
  return await fetchRoles();
}

export async function isCoach(): Promise<boolean> {
  const roles = await fetchRoles();
  return roles.includes("coach") || roles.includes("admin");
}

export async function isAdmin(): Promise<boolean> {
  const roles = await fetchRoles();
  return roles.includes("admin");
}

export function resetRoleCache() {
  clearRoleCache();
  rolePromise = null;
}

export async function ensureRole(role: string): Promise<void> {
  const normalized = role.toLowerCase();
  const roles = await fetchRoles();
  if (roles.includes(normalized)) return;
  const updatedRoles = await setCurrentUserRoles([...roles, normalized]);
  if (!updatedRoles.length) {
    applyRoleCache([...roles, normalized]);
  }
}

export async function ensureCoachRole(): Promise<void> {
  await ensureRole("coach");
}

const rolesEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export async function ensureCoachRoleOnly(): Promise<void> {
  const handles = resolveHandles();
  const auth = handles?.auth;
  const database = handles?.db;
  const uid = auth?.currentUser?.uid;
  if (!database || !uid) return;

  const ref = roleRef(database, uid);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() as RolesDocument : null;
  const roles = await fetchRoles();

  // Remove admin role if present
  const filtered = roles.filter((role) => role !== "admin");
  if (!filtered.includes("coach")) {
    filtered.push("coach");
  }

  // Only update if roles changed
  if (rolesEqual(filtered, roles)) return;

  // Build update payload preserving history
  const payload: Record<string, unknown> = {
    roles: filtered,
    updatedAt: serverTimestamp(),
  };

  // Don't include teamScopes in initial write - let updateCoachTeamScope handle it
  // Only preserve existing teamScopes if document already exists
  if (existing?.teamScopes && existing.teamScopes.length > 0) {
    payload.teamScopes = existing.teamScopes;
  }
  
  // Only include teamAnchor if it exists
  if (existing?.teamAnchor) {
    payload.teamAnchor = existing.teamAnchor;
  }
  
  // Preserve accessHistory if it exists
  if (existing?.accessHistory) {
    payload.accessHistory = existing.accessHistory;
  }

  // Preserve any existing coach access - only include defined values
  const accessHistoryEntry: Record<string, unknown> = {
    roles: ["coach"],
    teamScopes: existing?.teamScopes || [],
    lastUsed: serverTimestamp()
  };
  // Only add teamAnchor if it exists
  if (existing?.teamAnchor) {
    accessHistoryEntry.teamAnchor = existing.teamAnchor;
  }
  payload["accessHistory.2468"] = accessHistoryEntry;

  await setDoc(ref, payload, { merge: true });
  applyRoleCache(filtered);
}

export async function ensureAdminRole(): Promise<void> {
  const handles = resolveHandles();
  const auth = handles?.auth;
  const database = handles?.db;
  const uid = auth?.currentUser?.uid;
  if (!database || !uid) return;

  const ref = roleRef(database, uid);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() as RolesDocument : null;
  const roles = await fetchRoles();
  
  // If already admin, just ensure coach role
  if (roles.includes("admin")) {
    if (!roles.includes("coach")) {
      const updated = await setCurrentUserRoles([...roles, "coach"]);
      if (!updated.length) {
        applyRoleCache([...roles, "coach"]);
      }
    }
    return;
  }

  // Combine roles for admin - minimal payload
  const combined = ["admin", "coach"];
  
  // Build minimal update payload
  const payload: Record<string, unknown> = {
    roles: combined,
    updatedAt: serverTimestamp(),
  };

  await setDoc(ref, payload, { merge: true });
  applyRoleCache(combined);
}

type Lift = "bench" | "squat" | "deadlift" | "press";
type Week = 1 | 2 | 3 | 4;

export type SessionSet = {
  pct: number;
  weight: number;
  reps: number;
  status?: "S" | "F";
  actualReps?: number | null;
};
export type SessionPayload = {
  lift: Lift;
  week: Week;
  unit: Unit;
  tm: number;
  warmups: SessionSet[];
  work: SessionSet[];
  amrap: { weight: number; reps: number };
  est1rm: number;
  note?: string;
  pr?: boolean;
};

export type SessionRecord = SessionPayload & {
  id?: string;
  uid?: string;
  createdAt?: number | null;
  source?: "remote" | "local";
};

const SESSION_KEY = "pl.sessions.v1";

const readLocalSessions = (): SessionRecord[] => {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as SessionRecord[];
  } catch (err) {
    console.warn("Failed to read local sessions", err);
  }
  return [];
};

const writeLocalSessions = (rows: SessionRecord[]) => {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(rows));
  } catch (err) {
    console.warn("Failed to write local sessions", err);
  }
};

const persistLocalSession = (session: SessionRecord) => {
  const rows = readLocalSessions();
  rows.push(session);
  writeLocalSessions(rows);
};

const toMillis = (value: any): number => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value === "object") {
    if (typeof value.toMillis === "function") return value.toMillis();
    if (typeof value.seconds === "number") {
      return value.seconds * 1000 + Math.round((value.nanoseconds || 0) / 1e6);
    }
  }
  return 0;
};

const normalizeSetList = (value: any): SessionSet[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const pct = Number(item?.pct);
    const weight = Number(item?.weight);
    const reps = Number(item?.reps);
    const status =
      item?.status === "S" || item?.status === "F" ? (item.status as "S" | "F") : undefined;
    const rawActual =
      typeof item?.actualReps === "number"
        ? item.actualReps
        : typeof item?.actualReps === "string"
        ? Number(item.actualReps)
        : undefined;
    const actualReps =
      status === "F" && Number.isFinite(rawActual) && (rawActual as number) >= 0
        ? Number(rawActual)
        : undefined;
    return {
      pct: Number.isFinite(pct) ? pct : 0,
      weight: Number.isFinite(weight) ? weight : 0,
      reps: Number.isFinite(reps) ? reps : 0,
      status,
      actualReps,
    };
  });
};

const normalizeSession = (
  raw: any,
  overrides: Partial<SessionRecord> = {}
): SessionRecord => {
  const createdAt = toMillis(raw?.createdAt) || Date.now();
  return {
    lift: raw.lift,
    week: raw.week,
    unit: raw.unit,
    tm: raw.tm,
    warmups: normalizeSetList(raw.warmups),
    work: normalizeSetList(raw.work),
    amrap: raw.amrap || { weight: 0, reps: 0 },
    est1rm: raw.est1rm ?? 0,
    note: raw.note || "",
    pr: !!raw.pr,
    createdAt,
    ...overrides,
  };
};

export async function saveSession(
  payload: SessionPayload,
  targetUid?: string
): Promise<{ source: "remote" | "local" }> {
  const base = normalizeSession(payload, {
    createdAt: Date.now(),
    source: "local",
  });

  const handles = resolveHandles();
  const database = handles?.db;

  if (targetUid) {
    if (!database) {
      throw new Error("Firebase is not available for coach session save.");
    }
    const col = collection(database, "athletes", targetUid, "sessions");
    await addDoc(col, {
      ...payload,
      createdAt: serverTimestamp(),
    });
    return { source: "remote" };
  }

  let uid: string | null = null;
  try {
    uid = await getUid();
  } catch (err) {
    console.warn("saveSession getUid failed", err);
  }

  if (!uid || !database) {
    persistLocalSession({ ...base, uid: uid ?? LOCAL_UID });
    return { source: "local" };
  }

  const col = collection(database, "athletes", uid, "sessions");
  await addDoc(col, {
    ...payload,
    createdAt: serverTimestamp(),
  });

  return { source: "remote" };
}

export async function recentSessions(
  lift: Lift,
  count = 10,
  targetUid?: string
): Promise<SessionRecord[]> {
  const handles = resolveHandles();
  const database = handles?.db;

  if (targetUid) {
    if (!database) return [];
    try {
      const col = collection(database, "athletes", targetUid, "sessions");
      const fetchLimit = Math.max(count * 3, 25);
      const snap = await getDocs(
        query(col, orderBy("createdAt", "desc"), limit(fetchLimit))
      );
      const remote = snap.docs
        .map((docSnap) =>
          normalizeSession(docSnap.data(), {
            id: docSnap.id,
            uid: targetUid,
            source: "remote",
          })
        )
        .filter((s) => s.lift === lift);
      return remote.slice(0, count);
    } catch (err) {
      console.warn("recentSessions coach query failed", err);
      return [];
    }
  }

  const local = readLocalSessions().filter((s) => s.lift === lift);
  const localSorted = local
    .map((s) =>
      normalizeSession(s, { source: "local", uid: s.uid ?? LOCAL_UID })
    )
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  let uid: string | null = null;
  try {
    uid = await getUid();
  } catch (err) {
    console.warn("recentSessions getUid failed", err);
  }

  if (!uid || !database) {
    return localSorted.slice(0, count);
  }

  try {
    const col = collection(database, "athletes", uid, "sessions");
    const fetchLimit = Math.max(count * 3, 25);
    const snap = await getDocs(
      query(col, orderBy("createdAt", "desc"), limit(fetchLimit))
    );
    const remote = snap.docs
      .map((docSnap) =>
        normalizeSession(docSnap.data(), {
          id: docSnap.id,
          uid,
          source: "remote",
        })
      )
      .filter((s) => s.lift === lift);

    const combined = [...remote, ...localSorted];
    combined.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    return combined.slice(0, count);
  } catch (err) {
    console.warn("recentSessions query failed", err);
    return localSorted.slice(0, count);
  }
}


export async function getAllSessionsForProfile(
  targetUid?: string
): Promise<{ lift: string; sessions: SessionRecord[] }[]> {
  const lifts: Lift[] = ["squat", "bench", "deadlift", "press"];
  const results: { lift: string; sessions: SessionRecord[] }[] = [];
  
  for (const lift of lifts) {
    const sessions = await recentSessions(lift, 50, targetUid);
    if (sessions.length > 0) {
      // Sort by date ascending for chart display
      sessions.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
      results.push({ lift, sessions });
    }
  }
  
  return results;
}

export async function bestEst1RM(
  lift: Lift,
  sample = 10,
  targetUid?: string
): Promise<number> {
  const rows = await recentSessions(lift, sample, targetUid);
  const ests = rows
    .map((r) => (typeof r.est1rm === "number" ? r.est1rm : Number(r.est1rm)))
    .filter((v): v is number => typeof v === "number" && !isNaN(v));
  if (!ests.length) return 0;
  return Math.max(...ests);
}

export type AthleteCodeStatus = "ok" | "taken" | "unavailable";

export async function ensureAthleteCode(
  uid: string,
  code: string,
  orgId: string,
  previous?: string | null
): Promise<AthleteCodeStatus> {
  const trimmed = (code ?? "").trim();
  const safeOrgId = (orgId || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!trimmed || !safeOrgId) return "unavailable";

  const handles = resolveHandles();
  const database = handles?.db;
  if (!database) return "ok";

  // Use org-scoped document ID: orgId_code (e.g., "rt_1234")
  const codeDocId = `${safeOrgId}_${trimmed}`;
  const newRef = doc(database, "athleteCodes", codeDocId);
  const shouldClearPrevious = previous && previous !== trimmed;
  const prevDocId = shouldClearPrevious && previous ? `${safeOrgId}_${previous}` : null;
  const prevRef = prevDocId ? doc(database, "athleteCodes", prevDocId) : null;

  try {
    await runTransaction(database, async (tx) => {
      const existing = await tx.get(newRef);
      let prevSnap: DocumentSnapshot | null = null;
      if (prevRef) {
        prevSnap = await tx.get(prevRef);
      }

      if (existing.exists()) {
        const owner = existing.data()?.uid;
        if (owner && owner !== uid) {
          throw new Error("TAKEN");
        }
      }

      tx.set(newRef, { uid, orgId: safeOrgId, updatedAt: serverTimestamp() });

      if (prevRef && prevSnap?.exists()) {
        const owner = prevSnap.data()?.uid;
        if (owner === uid) {
          tx.delete(prevRef);
        }
      }
    });
    return "ok";
  } catch (err: any) {
    if (err?.message === "TAKEN") return "taken";
    console.warn("ensureAthleteCode transaction failed", err);
    return "unavailable";
  }
}

const generateCodeCandidate = (): string => {
  const value = Math.floor(Math.random() * 9000) + 1000;
  return String(value);
};

export type AssignAthleteAccessCodeResult = {
  status: AthleteCodeStatus;
  code: string;
  source: "remote" | "local";
};

export async function assignAthleteAccessCode(
  targetUid: string,
  code: string
): Promise<AssignAthleteAccessCodeResult> {
  const trimmed = (code ?? "").trim();
  if (!/^\d{4}$/.test(trimmed)) {
    return { status: "unavailable", code: trimmed, source: "local" };
  }

  let profile = await loadProfileRemote(targetUid);
  if (!profile) {
    profile = {
      uid: targetUid,
      firstName: "",
      lastName: "",
      unit: "lb",
      team: undefined,
      tm: {},
      oneRm: {},
      accessCode: null,
      equipment: defaultEquipment(),
    };
  }

  // Get orgId from the profile - required for org-scoped codes
  const orgId = profile.orgId || "";
  if (!orgId) {
    console.warn("Cannot assign athlete code without orgId in profile");
    return { status: "unavailable", code: trimmed, source: "local" };
  }

  const normalized: Profile = {
    ...profile,
    accessCode: trimmed,
    tm: profile.tm ?? {},
    oneRm: profile.oneRm ?? {},
    equipment: profile.equipment ?? defaultEquipment(),
  };

  const handles = resolveHandles();
  const database = handles?.db;
  if (!database) {
    saveProfileLocal(normalized);
    return { status: "ok", code: trimmed, source: "local" };
  }

  const status = await ensureAthleteCode(targetUid, trimmed, orgId, profile.accessCode ?? null);
  if (status === "taken") {
    return { status, code: trimmed, source: "remote" };
  }
  if (status !== "ok") {
    saveProfileLocal(normalized);
    return { status: "ok", code: trimmed, source: "local" };
  }

  try {
    await saveProfile(normalized);
    return { status: "ok", code: trimmed, source: "remote" };
  } catch (err) {
    console.warn("Failed to sync access code to Firestore, saved locally instead.", err);
    saveProfileLocal(normalized);
    return { status: "ok", code: trimmed, source: "local" };
  }
}

export async function regenerateAthleteCode(targetUid: string): Promise<string> {
  for (let attempt = 0; attempt < 25; attempt++) {
    const candidate = generateCodeCandidate();
    const result = await assignAthleteAccessCode(targetUid, candidate);
    if (result.status === "ok") {
      return result.code;
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }
  throw new Error("Could not reserve a unique code. Try again.");
}

export async function deleteAthlete(uid: string): Promise<void> {
  const handles = resolveHandles();
  const database = handles?.db;
  if (!database) {
    throw new Error("Firebase is required to delete athletes.");
  }

  const auth = handles?.auth;
  const profile = await loadProfileRemote(uid);
  const sessionsCol = collection(database, "athletes", uid, "sessions");
  let sessionRefs: DocumentReference[] = [];

  try {
    const snap = await getDocs(sessionsCol);
    sessionRefs = snap.docs.map((docSnap) => docSnap.ref);
  } catch (err) {
    console.warn("Failed to list sessions before deleting athlete", err);
  }

  if (sessionRefs.length) {
    const chunkSize = 400;
    for (let i = 0; i < sessionRefs.length; i += chunkSize) {
      const batch = writeBatch(database);
      sessionRefs.slice(i, i + chunkSize).forEach((ref) => batch.delete(ref));
      await batch.commit();
    }
  }

  const accessCodes = new Set<string>();
  if (profile?.accessCode) {
    accessCodes.add(profile.accessCode);
  } else {
    try {
      const codeSnap = await getDocs(
        query(collection(database, "athleteCodes"), where("uid", "==", uid))
      );
      codeSnap.forEach((docSnap) => accessCodes.add(docSnap.id));
    } catch (err) {
      console.warn("Failed to query athlete code mapping for deletion", err);
    }
  }

  const cleanupBatch = writeBatch(database);
  cleanupBatch.delete(profRef(database, uid));
  accessCodes.forEach((code) =>
    cleanupBatch.delete(doc(database, "athleteCodes", code))
  );

  try {
    await cleanupBatch.commit();
  } catch (err) {
    console.warn("Failed to cleanup athlete records", err);
    throw err;
  }

  try {
    await deleteDoc(roleRef(database, uid));
  } catch (err) {
    console.warn(`Failed to remove role mapping for ${uid}`, err);
  }

  if (auth?.currentUser?.uid === uid) {
    return;
  }

  try {
    const queueRef = doc(collection(database, "__deleteAuthUser__"), uid);
    await setDoc(queueRef, { uid, requestedAt: serverTimestamp() });
  } catch (err) {
    console.warn(`Failed to queue auth deletion for ${uid}`, err);
  }
}

export async function fetchAthleteSessions(
  uid: string,
  count = 12
): Promise<SessionRecord[]> {
  const handles = resolveHandles();
  const database = handles?.db;
  if (!database) return [];
  try {
    const col = collection(database, "athletes", uid, "sessions");
    const snap = await getDocs(
      query(col, orderBy("createdAt", "desc"), limit(Math.max(count, 1)))
    );
    return snap.docs.map((docSnap) =>
      normalizeSession(docSnap.data(), {
        id: docSnap.id,
        uid,
        source: "remote",
      })
    );
  } catch (err) {
    console.warn(`fetchAthleteSessions failed for ${uid}`, err);
    return [];
  }
}

// ---- Organization Management ----
export type OrgConfig = {
  name: string;
  abbr: string;
  logo: string;
  orgCode: string;
  coachPasscode: string;
  primaryColor?: string;
  secondaryColor?: string;
  adminEmail?: string;
  adminPhone?: string;
  adminFirstName?: string;
  adminLastName?: string;
  loginPath?: string;
  createdAt?: number;
  updatedAt?: number;
};

export async function fetchOrgConfig(orgId: string): Promise<OrgConfig | null> {
  const database = fb.db;
  if (!database) return null;
  
  try {
    const orgRef = doc(database, "organizations", orgId.toUpperCase());
    const snap = await getDoc(orgRef);
    
    if (!snap.exists()) return null;
    
    const data = snap.data();
    return {
      name: data.name || orgId,
      abbr: data.abbr || orgId,
      logo: data.logo || "/assets/dragon.png",
      orgCode: data.orgCode || "",
      coachPasscode: data.coachPasscode || "",
      primaryColor: data.primaryColor,
      secondaryColor: data.secondaryColor,
      adminEmail: data.adminEmail,
      adminPhone: data.adminPhone,
      adminFirstName: data.adminFirstName,
      adminLastName: data.adminLastName,
      loginPath: data.loginPath,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  } catch (err) {
    console.warn("Failed to fetch org config", err);
    return null;
  }
}

export const buildCoachEmail = (firstName: string, lastName: string, orgId: string): string => {
  const canonical = `${firstName}${lastName}`.toLowerCase().replace(/[^a-z]/g, "");
  const safeOrgId = (orgId || "pl").toLowerCase().replace(/[^a-z0-9]/g, "");
  // Use a proper email format that Firebase accepts
  return `coach-${canonical}-${safeOrgId}@anchorone.app`;
};

export const coachPassword = (orgCode: string, coachPasscode: string) => {
  // Firebase requires passwords to be at least 6 characters
  const base = `${orgCode}-${coachPasscode}`;
  // Pad with a constant suffix if too short
  return base.length >= 6 ? base : base + "!coach";
};
